import * as Popover from "@radix-ui/react-popover";
import { ExternalLink } from "lucide-react";
import { type MouseEvent, type PointerEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { CopyValue } from "@/components/copy-value";
import { type AssetIdentity, cardanoscanAsset, LOVELACE, policyId, unitId } from "@/lib/cardanoscan";
import { cn } from "@/lib/utils/cn";

// A ticker is ambiguous, so every asset name in the UI opens a card with its on-chain identity
// and a Cardanoscan link. [[knowledge-explorer-links]]

// Long enough that sweeping the pointer across a table does not flash cards open; the close delay
// lets the pointer cross the gap into the card to reach its link.
const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 200;

interface AssetLabelProps extends AssetIdentity {
    /** A control to hang the card on, such as a copy button or a link; by default, the name. */
    children?: ReactNode;
    className?: string;
}

/**
 * Opens on mouse hover; a tap or a keyboard press toggles it, since a phone has no hover. A mouse
 * click is left to the wrapped control, so it still copies or navigates as before.
 */
export function AssetLabel({ id, name, children, className }: AssetLabelProps) {
    const [open, setOpen] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const lastPointer = useRef<string>("");
    const anchor = useRef<HTMLSpanElement>(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const openAfter = (event: PointerEvent, next: boolean, delay: number) => {
        if (event.pointerType !== "mouse") return;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setOpen(next), delay);
    };
    const onPointerEnter = (event: PointerEvent) => openAfter(event, true, OPEN_DELAY_MS);
    const onPointerLeave = (event: PointerEvent) => openAfter(event, false, CLOSE_DELAY_MS);
    const onClick = (event: MouseEvent) => {
        // `detail` is 0 for a click synthesised from the keyboard.
        const byMouse = lastPointer.current === "mouse" && event.detail > 0;
        clearTimeout(timer.current);
        setOpen((wasOpen) => (byMouse ? true : !wasOpen));
    };

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Anchor asChild>
                <span
                    ref={anchor}
                    className={cn("inline-flex min-w-0", className)}
                    onPointerDown={(event) => {
                        lastPointer.current = event.pointerType;
                    }}
                    onPointerEnter={onPointerEnter}
                    onPointerLeave={onPointerLeave}
                    onClick={onClick}
                >
                    {children ?? (
                        <button
                            type="button"
                            aria-expanded={open}
                            aria-haspopup="dialog"
                            className="cursor-help rounded-sm underline decoration-muted-foreground/50 decoration-dotted underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                        >
                            {name}
                        </button>
                    )}
                </span>
            </Popover.Anchor>
            <Popover.Portal>
                <Popover.Content
                    side="top"
                    align="start"
                    sideOffset={6}
                    collisionPadding={8}
                    aria-label={`${name} asset`}
                    // A card opened by hover must not pull focus away from where the operator is.
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                    // The anchor's own click toggles the card; letting the outside-press close it
                    // first would make that click reopen it.
                    onInteractOutside={(event) => {
                        if (anchor.current?.contains(event.target as Node)) event.preventDefault();
                    }}
                    onPointerEnter={onPointerEnter}
                    onPointerLeave={onPointerLeave}
                    className="z-50 w-[min(24rem,calc(100vw-1rem))] space-y-2.5 rounded-lg border bg-card p-3 text-sm text-card-foreground shadow-lg"
                >
                    <AssetCard id={id} name={name} />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

function AssetCard({ id, name }: AssetIdentity) {
    return (
        <>
            <p className="font-semibold">{name}</p>
            <dl className="space-y-2">
                {id === LOVELACE ? (
                    <IdField label="Unit" value={LOVELACE} />
                ) : (
                    <>
                        <IdField label="Policy ID" value={policyId(id)} />
                        <IdField label="Asset ID" value={unitId(id)} />
                    </>
                )}
            </dl>
            <a
                href={cardanoscanAsset(id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline"
            >
                View on Cardanoscan
                <ExternalLink aria-hidden className="size-3.5 text-muted-foreground" />
            </a>
        </>
    );
}

function IdField({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="font-mono text-xs">
                <CopyValue value={value} label={label} />
            </dd>
        </div>
    );
}
